import { LitElement, PropertyValues, html, nothing } from 'lit';
import { property, query } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import { guardedCustomElement } from '../utils/guarded-custom-element';
import { styles } from './sds-radio.styles';
import { SDS_SIZES } from '../types';
import { SDSDelegateInteractionMixin, SDSEventDispatcherMixin } from '../mixins';

type RadioSize = Extract<SDS_SIZES, 'lg' | 'md' | 'sm'>;

export interface SDSRadioProps {
	disabled?: boolean;
	required?: boolean;
	label?: string;
	'hide-label'?: boolean;
	name?: string;
	value?: string;
	checked?: boolean;
	error?: boolean;
	readonly?: boolean;
	size?: RadioSize;
	'help-text'?: string;
	parentRequestDisabled?: boolean;
}

@guardedCustomElement('sds-radio')
export class SDSRadio extends SDSDelegateInteractionMixin(SDSEventDispatcherMixin(LitElement)) {
	// Styling
	static override styles = [styles];

	static formAssociated = true;
	public readonly internals: ElementInternals;

	static override shadowRootOptions: ShadowRootInit = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true,
	};

	@property({ type: Boolean, reflect: true }) required: SDSRadioProps['required'] = false;
	@property({ type: String, reflect: true }) label: SDSRadioProps['label'] = '';
	@property({ type: Boolean, reflect: true, attribute: 'hide-label' }) hideLabel: SDSRadioProps['hide-label'] = false;
	@property({ type: String, reflect: true }) name?: SDSRadioProps['name'];
	@property({ type: String }) value: SDSRadioProps['value'] = 'on';
	@property({ type: Boolean, reflect: true }) checked: SDSRadioProps['checked'] = false;
	@property({ type: Boolean, reflect: true }) error: SDSRadioProps['error'] = false;
	@property({ type: Boolean, attribute: 'readonly', reflect: true }) readOnly: SDSRadioProps['readonly'] = false;
	@property({ type: String, reflect: true }) size: SDSRadioProps['size'] = 'md';
	@property({ type: String, attribute: 'help-text' }) helpText?: SDSRadioProps['help-text'];
	@property({ type: Boolean, reflect: true })

	/**
	 * disabled:
	 * - If set directly (prop/attr), this radio becomes "self-controlled"
	 *   and ignores parentRequestDisabled.
	 * - If not self-controlled, this follows parentRequestDisabled (group/form).
	 */
	get disabled(): SDSRadioProps['disabled'] {
		return this._disabled;
	}
	set disabled(value: SDSRadioProps['disabled']) {
		const newVal = value ?? false;
		const oldVal = this._disabled;

		if (oldVal === newVal) return;

		this._disabled = newVal;
		this.requestUpdate('disabled', oldVal);

		if (!this._settingDisabledFromParent) {
			this._disabledIsAuthorControlled = newVal;
		}
	}

	/**
	 * parentRequestDisabled:
	 * - Set by SDSRadioGroup to request disabled/enabled.
	 * - Only applied if SDSRadio is NOT self-controlled.
	 */
	@property({ type: Boolean, attribute: false })
	get parentRequestDisabled(): SDSRadioProps['parentRequestDisabled'] {
		return this._parentRequestDisabled;
	}
	set parentRequestDisabled(value: SDSRadioProps['parentRequestDisabled']) {
		const newVal = value ?? false;
		const oldVal = this._parentRequestDisabled;

		if (oldVal === newVal) return;

		this._parentRequestDisabled = newVal;
		this.requestUpdate('parentRequestDisabled', oldVal);

		if (this._disabledIsAuthorControlled) return;

		this._settingDisabledFromParent = true;
		this.disabled = newVal;
		this._settingDisabledFromParent = false;
	}

	@query('#control') control!: HTMLInputElement;

	private _disabled: SDSRadioProps['disabled'] = false;
	private _disabledIsAuthorControlled: boolean = false;
	private _settingDisabledFromParent: boolean = false;
	private _parentRequestDisabled: SDSRadioProps['parentRequestDisabled'] = false;
	private _initialChecked: boolean = false;

	constructor() {
		super();
		this.internals = this.attachInternals();
	}

	override connectedCallback() {
		super.connectedCallback();
		this.internals.role = 'radio';
		if (!this._initialChecked) {
			this._initialChecked = this.checked ?? false;
		}
		this.addEventListener('keydown', this._handleKeydown);
	}

	override disconnectedCallback() {
		super.disconnectedCallback();
		this.removeEventListener('keydown', this._handleKeydown);
	}

	override firstUpdated(_changedProperties: PropertyValues): void {
		super.firstUpdated(_changedProperties);
		this._updateGroupValidation();
	}

	override updated(changedProperties: Map<string | number | symbol, unknown>) {
		super.updated(changedProperties);

		if (changedProperties.has('checked')) {
			if (this.checked) {
				this._uncheckSiblings();
				this.internals.setFormValue(this.value ?? null);
			} else {
				this.internals.setFormValue(null);
			}
			this._updateGroupValidation();
		}

		if (changedProperties.has('required') && this.control) {
			this.control.required = this.required ?? false;
		}

		if (!this._disabledIsAuthorControlled && this.disabled !== this._parentRequestDisabled) {
			this._settingDisabledFromParent = true;
			this.disabled = this._parentRequestDisabled ?? false;
			this._settingDisabledFromParent = false;
		}
	}

	private _getGroupRadios(): SDSRadio[] {
		if (!this.name) return [this];
		const root = this.getRootNode() as Document | ShadowRoot;
		const radioGroup = Array.from(root.querySelectorAll<SDSRadio>(`sds-radio[name="${this.name}"]`));
		return radioGroup;
	}

	private _updateGroupValidation() {
		const group: Array<SDSRadio> = this._getGroupRadios();
		const isAnyChecked = group.some((radio) => radio.checked);
		const isAnyRequired = group.some((radio) => radio.required);
		group.forEach((radio) => {
			if (!radio.control) return;
			if (isAnyRequired) {
				radio.internals.setValidity(isAnyChecked ? {} : { valueMissing: true }, isAnyChecked ? '' : radio.control.validationMessage, radio.control);
			} else {
				radio.internals.setValidity({});
			}
		});
	}

	private readonly _handleKeydown = (event: KeyboardEvent) => {
		if (!this.name || this.disabled || this.readOnly) return;

		const root = this.getRootNode() as Document | ShadowRoot;
		const radios = Array.from(root.querySelectorAll<SDSRadio>(`sds-radio[name="${this.name}"]`));
		const currentIndex = radios.indexOf(this);

		if (currentIndex === -1) return;

		let targetIndex: number | null = null;

		switch (event.key) {
			case 'ArrowUp':
			case 'ArrowLeft':
				targetIndex = currentIndex > 0 ? currentIndex - 1 : radios.length - 1;
				break;
			case 'ArrowDown':
			case 'ArrowRight':
				targetIndex = currentIndex < radios.length - 1 ? currentIndex + 1 : 0;
				break;
			default:
				return;
		}

		const targetRadio = radios[targetIndex];
		targetRadio?.focus();
		targetRadio?.click();
		event.preventDefault();
	};

	formResetCallback() {
		this.checked = this._initialChecked;
		this.control.checked = this._initialChecked;
		this.internals.setFormValue(this.checked ? this.value ?? 'on' : null);
		this._updateGroupValidation();
	}

	private _uncheckSiblings() {
		if (!this.name) return;
		const radios: Array<SDSRadio> = this._getGroupRadios();

		for (const radio of radios) {
			if (radio !== this && radio.checked) {
				radio.checked = false;
				radio.internals.setFormValue(null);
			}
		}
	}

	private readonly _handleChange = (event: Event) => {
		if (this.disabled || this.readOnly) {
			(event.target as HTMLInputElement).checked = this.checked ?? false;
			event.preventDefault();
			return;
		}

		const elem = event.target as HTMLInputElement;
		this.checked = elem.checked;
		if (this.checked) {
			this._uncheckSiblings();
		}
		this._updateGroupValidation();
		this.dispatchSheriffEvent('change');
	};

	private readonly _handleFocus = (event: Event) => {
		event.stopImmediatePropagation();
		this.dispatchSheriffEvent('focus');
	};

	private readonly _handleBlur = (event: Event) => {
		event.stopImmediatePropagation();
		this.dispatchSheriffEvent('blur');
	};

	get form() {
		return this.internals.form;
	}

	get type(): string {
		return 'radio';
	}

	get validity() {
		return this.internals.validity;
	}

	get validationMessage() {
		return this.internals.validationMessage;
	}

	get willValidate() {
		return this.internals.willValidate;
	}

	checkValidity(): boolean {
		return this.internals.checkValidity();
	}

	reportValidity(): boolean {
		return this.internals.reportValidity();
	}

	setCustomValidity(message: string): void {
		this.internals.setValidity({ customError: !!message }, message);
	}

	formDisabledCallback(disabled: boolean): void {
		this._settingDisabledFromParent = true;
		this.disabled = disabled;
		this._settingDisabledFromParent = false;

		if (this.control) this.control.disabled = disabled;
	}

	override render() {
		const radioClasses = {
			'sds-radio': true,
			'sds-radio--error': Boolean(this.error),
		};

		const inputClasses = {
			'sds-radio__input': true,
			'sds-radio__input--error': Boolean(this.error),
		};

		return html`
			<div class="${classMap(radioClasses)}">
				<input
					id="control"
					type="radio"
					name="${ifDefined(this.name)}"
					value="${ifDefined(this.value)}"
					?checked="${this.checked ?? false}"
					.checked="${this.checked ?? false}"
					?disabled="${this.disabled ?? false}"
					.disabled="${this.disabled ?? false}"
					?required="${this.required}"
					?readonly="${this.readOnly}"
					@change="${this._handleChange}"
					@focus="${this._handleFocus}"
					@blur="${this._handleBlur}"
					class="${classMap(inputClasses)}"
				/>
				${this.label ? html`<label class="sds-radio__label" for="control">${this.label}</label>` : nothing}
				${this.helpText ? html`<span class="sds-radio__help-text">${this.helpText}</span>` : nothing}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'sds-radio': SDSRadio;
	}
}
